<template>
  <Panel
    :header="filterHeaderString"
    :toggleable="true"
    :collapsed="true"
    class="mt-3"
  >
    <form
      @submit.prevent="validateFilters"
    >
      <div class="p-input mb-3">
        <label
          for="stakeholderId"
        >{{ $t('entity.lineId.stakeholderId') }}</label>
        <InputText
          v-if="stakeholder === null"
          id="stakeholderId"
          v-model.trim="v$.filterStakeholderId.$model"
          class="col-12 mt-2"
          type="text"
          :class="{'invalid-input': v$.filterStakeholderId.$error }"
          autocomplete="on"
        />
        <InputText
          v-else
          id="stakeholderId"
          class="col-12 mt-2"
          type="text"
          disabled
          :value="stakeholder.stakeholderId"
        />
      </div>
      <div class="grid">
        <div class="col">
          <div class="p-input mb-3">
            <label
              for="duplexHigh"
            >{{ $t('entity.lineId.duplexHigh') }}</label>
            <InputText
              id="duplexHigh"
              v-model.trim="v$.filterDuplexHigh.$model"
              class="col-12 mt-2"
              type="text"
              :class="{'invalid-input': v$.filterDuplexHigh.$error }"
              autocomplete="on"
            />
          </div>
        </div>
        <div class="col">
          <div class="p-input mb-3">
            <label
              for="duplexLow"
            >{{ $t('entity.lineId.duplexLow') }}</label>
            <InputText
              id="duplexLow"
              v-model.trim="v$.filterDuplexLow.$model"
              class="col-12 mt-2"
              type="text"
              :class="{'invalid-input': v$.filterDuplexLow.$error }"
              autocomplete="on"
            />
          </div>
        </div>
      </div>
      <div class="grid">
        <div class="col">
          <div class="p-input mb-3">
            <label
              for="tuningRangeFrom"
            >{{ $t('entity.lineId.tuningRangeFrom') }}</label>
            <InputNumber
              id="tuningRangeFrom"
              v-model="v$.filterTuningRangeFrom.$model"
              mode="decimal"
              :min-fraction-digits="1"
              :max-fraction-digits="5"
              :min="1"
              :max="999999"
              class="col-12 p-0 mt-2"
              :class="{'invalid-input': v$.filterTuningRangeFrom.$error }"
              autocomplete="on"
            />
          </div>
        </div>
        <div class="col">
          <div class="p-input mb-3">
            <label
              for="tuningRangeTo"
            >{{ $t('entity.lineId.tuningRangeTo') }}</label>
            <InputNumber
              id="tuningRangeTo"
              v-model="v$.filterTuningRangeTo.$model"
              mode="decimal"
              :min-fraction-digits="1"
              :max-fraction-digits="5"
              :min="1"
              :max="999999"
              class="col-12 p-0 mt-2"
              :class="{'invalid-input': v$.filterTuningRangeTo.$error }"
              autocomplete="on"
            />
          </div>
        </div>
      </div>
      <div class="grid">
        <div class="col-12 md:col-6">
          <div class="p-input mb-3" v-if="!replaceLicencePeriod">
            <label
              for="licencePeriods"
            >{{ $t('entity.lineId.listLicencePeriods') }}</label>
            <MultiSelect
              id="licencePeriods"
              v-model="v$.filterLicencePeriod.$model"
              class="col-12 p-0 mt-2"
              :class="{'invalid-input': v$.filterLicencePeriod.$error }"
              :options="periods"
              :option-label="(value) => $t(`entity.lineId.licencePeriods.${value}`)"
            />
            <div
              v-for="error of v$.filterLicencePeriod.$errors"
              :key="error.$uid"
              class="input-errors"
            >
              <div class="error-msg">
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="p-input mb-3" v-else>
            <label
              for="spectrumServices"
            >{{ $t('entity.lineId.spectrumService') }}</label>
            <Dropdown
              id="spectrumServices"
              v-model="v$.filterSpectrumService.$model"
              class="col-12 p-0 mt-2"
              :class="{'invalid-input': v$.filterSpectrumService.$error }"
              :options="spectrums"
              :option-label="(value) => $t(`${value}`)"
            />
            <div
              v-for="error of v$.filterSpectrumService.$errors"
              :key="error.$uid"
              class="input-errors"
            >
              <div class="error-msg">
                {{ error.$message }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 md:col-6">
          <div class="p-input mb-3">
            <label
              for="mainVenue"
            >{{ $t('entity.lineId.listMainVenues') }}</label>
            <MultiSelect
              id="mainVenues"
              v-model="v$.filterMainVenue.$model"
              class="col-12 p-0 mt-2"
              :class="{'invalid-input': v$.filterMainVenue.$error }"
              :options="venues"
              option-value="id"
              :option-label="(value) => $i18n.locale === 'fr' ? value.frenchWording : value.englishWording"
            />
            <div
              v-for="error of v$.filterMainVenue.$errors"
              :key="error.$uid"
              class="input-errors"
            >
              <div class="error-msg">
                {{ error.$message }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Message
        v-if="errors"
        severity="error"
        :closable="false"
      >
        <ul class="list-none m-0 p-0">
          <li
            v-for="error of errors"
            :key="error.content"
          >
            {{ $t(error) }}
          </li>
        </ul>
      </Message>
      <div class="flex justify-content-end">
        <Button
          :label="$t('action.reset')"
          icon="pi pi-times"
          class="p-button-rounded p-button-outlined p-button-secondary mr-2"
          @click="$emit('reset')"
        />
        <Button
          :label="$t('action.search')"
          type="submit"
          icon="pi pi-check"
          class="p-button-rounded p-button-outlined"
        />
      </div>
    </form>
  </Panel>
</template>

<script>
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Panel from "primevue/panel";
import Message from "primevue/message";
import Button from "primevue/button";
import {useVuelidate} from "@vuelidate/core";
import MultiSelect from "primevue/multiselect";
import Dropdown from 'primevue/dropdown';
import {reactive} from "vue";
import useVenueFilter from "@/composables/filters/useVenueFilter";

export default {
  name: "LineIdAdvancedFilter",
  components: {InputText, InputNumber, Panel, Message, Button, MultiSelect, Dropdown},
  props: {
    stakeholder: {
      type: Object,
      default: null
    },
    stakeholderId: {
      type: String,
      default: null
    },
    mainVenue: {
      type: Array,
      default: () => []
    },
    licencePeriod: {
      type: Array,
      default: () => []
    },
    spectrumService: {
      type: Array,
      default: () => []
    },
    tuningRangeFrom: {
      type: Number,
      default: null
    },
    tuningRangeTo: {
      type: Number,
      default: null
    },
    duplexHigh: {
      type: String,
      default: null
    },
    duplexLow: {
      type: String,
      default: null
    },
    replaceLicencePeriod: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:stakeholderId', 'update:mainVenue', 'update:licencePeriod', 'update:spectrumService', 'update:tuningRangeFrom', 'update:tuningRangeTo', 'update:duplexHigh', 'update:duplexLow', 'validate', 'reset'],
  setup() {
    const venueFilter = reactive(useVenueFilter());
    return {
      v$: useVuelidate(),
      venueFilter: venueFilter,
    }
  },
  data() {
    return {
      uniqueSiteCode: this.$store.getters['setting/getUniqueSiteCode'],
      venues: [],
      periods: [],
      spectrums: [],
      isSaving: false,
      errors: null,
    }
  },
  computed: {
    uniqueSiteMode() {
      return this.uniqueSiteCode && this.uniqueSiteCode !== '';
    },
    filterStakeholderId: {
      get() {
        return this.stakeholderId
      },
      set(value) {
        this.$emit('update:stakeholderId', value)
      }
    },
    filterMainVenue: {
      get() {
        return this.mainVenue
      },
      set(value) {
        this.$emit('update:mainVenue', value)
      }
    },
    filterLicencePeriod: {
      get() {
        return this.licencePeriod
      },
      set(value) {
        this.$emit('update:licencePeriod', value)
      }
    },
    filterSpectrumService: {
      get() {
        return this.spectrumService
      },
      set(value) {
        this.$emit('update:spectrumService', value)
      }
    },
    filterTuningRangeFrom: {
      get() {
        return this.tuningRangeFrom
      },
      set(value) {
        this.$emit('update:tuningRangeFrom', value)
      }
    },
    filterTuningRangeTo: {
      get() {
        return this.tuningRangeTo
      },
      set(value) {
        this.$emit('update:tuningRangeTo', value)
      }
    },
    filterDuplexHigh: {
      get() {
        return this.duplexHigh
      },
      set(value) {
        this.$emit('update:duplexHigh', value)
      }
    },
    filterDuplexLow: {
      get() {
        return this.duplexLow
      },
      set(value) {
        this.$emit('update:duplexLow', value)
      }
    },
    filterHeaderString() {
      const amountVenuesSelected = this.filterMainVenue?.filter(item => item !== "").length;
      const venuesSelected = this.venues.filter(venue => this.filterMainVenue.includes(venue.id)).map(venue => this.$i18n.locale === 'fr' ? venue.frenchWording : venue.englishWording).toString().replaceAll(',', ', ');
      const filterStr = (amountVenuesSelected && amountVenuesSelected > 0) ? `( ${venuesSelected} )` : '';
      return this.$t('lineidpage.advancedFilters') + ' ' + filterStr;
    }
  },
  validations() {
    return {
      filterStakeholderId: {},
      filterMainVenue: {},
      filterLicencePeriod: {},
      filterSpectrumService: {},
      filterTuningRangeFrom: {},
      filterTuningRangeTo: {},
      filterDuplexHigh: {},
      filterDuplexLow: {}
    }
  },
  watch: {
    async filterLicencePeriod() {
      if (this.filterLicencePeriod.length !== 0) {
        this.venues = await this.$lineIdService.getMainVenuesByLicencePeriods(this.filterLicencePeriod);
      }

      this.filterMainVenue = [];
    }
  },
  async mounted() {
    this.venues = (await this.$venueService.fetchVenuesPagingAndSorting(this.venueFilter.getQueryParams())).content;

    this.periods = this.$store.getters['setting/getLineIdLicencePeriods'].slice().sort((a,b) => {
      const nameA = this.$t(`entity.lineId.licencePeriods.${a}`).toLowerCase();
      const nameB = this.$t(`entity.lineId.licencePeriods.${b}`).toLowerCase();
      return nameA.localeCompare(nameB);
    });
    this.spectrums = await this.$lineIdService.getSpectrumServices();
  },
  methods: {
    async validateFilters() {
      this.errors = null;
      const isFormValid = await this.v$.$validate();
      if (!isFormValid) return;
      let venueCopy;
      if(this.venues.length === this.filterMainVenue.length) {
        venueCopy = [...this.filterMainVenue];
        this.filterMainVenue = [];
      }
      let periodCopy;
      if(this.periods.length === this.filterLicencePeriod.length) {
        periodCopy = [...this.filterLicencePeriod];
        this.filterLicencePeriod = [];
      }
      let spectrumServiceCopy;
      if(this.filterSpectrumService != null && this.spectrums.length === this.filterSpectrumService.length) {
        spectrumServiceCopy = [...this.filterSpectrumService];
        this.filterSpectrumService = [];
      }

      this.$emit('validate');

      if(venueCopy) this.filterMainVenue = venueCopy;
      if(periodCopy) this.filterLicencePeriod = periodCopy;
      if(spectrumServiceCopy) this.filterSpectrumService = spectrumServiceCopy;
    }
  }
}
</script>

<style scoped>

</style>
